import { useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  CreditCard, 
  DollarSign, 
  AlertCircle, 
  Settings, 
  FileText,
  BarChart3,
  Plug,
  ListChecks,
  Target
} from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface RouteConfig {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

const routeConfig: Record<string, RouteConfig> = {
  'dashboard': {
    label: 'Dashboard',
    icon: Home,
  },
  'customers': {
    label: 'Customers',
    icon: Users,
  },
  'transactions': {
    label: 'Transactions',
    icon: CreditCard,
  },
  'payments': {
    label: 'Payments',
    icon: DollarSign,
  },
  'collections': {
    label: 'Collections',
    icon: ListChecks,
  },
  'queue': {
    label: 'Collections Queue',
    icon: ListChecks,
  },
  'case': {
    label: 'Case',
    icon: FileText,
  },
  'promises': {
    label: 'Promise to Pay',
    icon: ListChecks,
  },
  'strategies': {
    label: 'Strategies',
    icon: Target,
  },
  'disputes': {
    label: 'Disputes',
    icon: AlertCircle,
  },
  'credit-scoring': {
    label: 'Credit Scoring',
    icon: BarChart3,
  },
  'settings': {
    label: 'Settings',
    icon: Settings,
  },
  'users': {
    label: 'Users',
    icon: Users,
  },
  'roles': {
    label: 'Roles & Permissions',
    icon: Settings,
  },
  'templates': {
    label: 'Templates',
    icon: FileText,
  },
  'edit': {
    label: 'Edit Template',
    icon: FileText,
  },
  'integrations': {
    label: 'Integrations',
    icon: Plug,
  },
};

export function useBreadcrumbs(): BreadcrumbItem[] {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Always start with Dashboard
  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: 'Dashboard',
      href: '/',
      icon: Home,
    },
  ];

  // Build breadcrumbs from path segments
  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    const config = routeConfig[segment];
    
    // Skip 'roles' segment - it will be handled by modifying the 'users' breadcrumb
    if (segment === 'roles' && pathSegments[index - 1] === 'users') {
      return;
    }
    
    // Handle role IDs (when parent is 'roles')
    if (!config && pathSegments[index - 1] === 'roles') {
      // This is a role ID, format it nicely
      const roleName = segment
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      breadcrumbs.push({
        label: roleName,
        href: currentPath,
      });
    }
    // Special handling for 'users' when followed by 'roles' - show as "Roles & Permissions"
    else if (segment === 'users' && pathSegments[index + 1] === 'roles') {
      breadcrumbs.push({
        label: 'Roles & Permissions',
        href: `${currentPath}?tab=roles`,
        icon: Settings,
      });
    }
    // Handle promise IDs (format: PTP-00001, etc.)
    else if (!config && /^PTP-\d+$/i.test(segment)) {
      // This is a promise ID
      breadcrumbs.push({
        label: segment,
        href: currentPath,
      });
    }
    // Handle case IDs (format: AR-00050, etc.)
    else if (!config && /^[A-Z]+-\d+$/i.test(segment)) {
      // This is a case ID
      breadcrumbs.push({
        label: `Case ${segment}`,
        href: currentPath,
      });
    }
    // Handle dynamic segments (like UUIDs or other IDs)
    else if (!config && /^[0-9a-f-]+$/i.test(segment)) {
      // This looks like an ID, use a generic label
      const parentSegment = pathSegments[index - 1];
      const parentConfig = routeConfig[parentSegment];
      
      breadcrumbs.push({
        label: parentConfig?.label.slice(0, -1) || 'Detail', // Remove 's' from plural
        href: currentPath,
      });
    } else if (config) {
      breadcrumbs.push({
        label: config.label,
        href: currentPath,
        icon: config.icon,
      });
    } else {
      // Fallback: capitalize the segment
      breadcrumbs.push({
        label: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
        href: currentPath,
      });
    }
  });

  return breadcrumbs;
}
